import React, { useEffect, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, FormControlLabel,
  MenuItem, Paper, Stack, Switch, TextField, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useParams, useSearchParams } from 'react-router-dom';
import * as api from '../plannerApi';
import * as T from '../types';
import {
  ALERT_TIMES, RECURRENCES, TASK_ALERTS, TASK_CATEGORIES, WEEK_DAYS, categoryText, dayOfWeekOf, shortDay, time, toTimeOnly, toggleIn, today, withPendingAlias,
} from '../labels';
import { NeedsHousehold, PageHeader, useConfirm, useHousehold } from '../common';
import { AliasEditor, useGoBack } from '../editing';

const plusOneMonth = (date: T.DateOnly) => {
  const [y, m, d] = date.split('-').map(Number);
  const next = new Date(y, m, d);
  return `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}-${String(next.getDate()).padStart(2, '0')}`;
};

/** Create (/casa/tarefas/nova?data=) or edit (/casa/tarefas/:taskId) a task and its schedule. */
const ALERT_HINTS: Record<T.TaskAlert, string> = {
  Notification: 'Uma notificação no telemóvel de quem faz. Sem hora certa, avisa às 09:00.',
  Alarm: 'Toca com som de alarme no telemóvel de quem faz, mesmo em silêncio, até ser marcada como feita.',
  None: 'Não avisa. Continua a aparecer na lista e no resumo do dia.',
};

const TaskEditPage: React.FC = () => {
  const { taskId } = useParams();
  const [search] = useSearchParams();
  const { members } = useHousehold();
  const goBack = useGoBack('/casa/tarefas');
  const [confirm, confirmDialog] = useConfirm();
  const isNew = !taskId;
  const initialDate = search.get('data') ?? today();

  const [loading, setLoading] = useState(!isNew);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [source, setSource] = useState<T.TaskSource>('Manual');
  const [mealLabel, setMealLabel] = useState<string | null>(null);

  const [title, setTitle] = useState('');
  const [assignee, setAssignee] = useState('');
  const [type, setType] = useState<T.RecurrenceType>('None');
  const [start, setStart] = useState<T.DateOnly>(initialDate);
  const [interval, setIntervalText] = useState('1');
  const [days, setDays] = useState<T.DayOfWeek[]>([dayOfWeekOf(initialDate)]);
  const [dayOfMonth, setDayOfMonth] = useState('');
  const [lastDay, setLastDay] = useState(false);
  const [times, setTimes] = useState('1');
  const [hasEnd, setHasEnd] = useState(false);
  const [end, setEnd] = useState<T.DateOnly>(plusOneMonth(today()));
  const [hasTime, setHasTime] = useState(false);
  const [dueTime, setDueTime] = useState('20:00');
  const [alertKind, setAlertKind] = useState<T.TaskAlert>('Notification');
  const [alertMinutes, setAlertMinutes] = useState(0);
  const [checklist, setChecklist] = useState('');
  const [notes, setNotes] = useState('');
  const [category, setCategory] = useState<T.TaskCategory>('Home');
  const [aliases, setAliases] = useState<string[]>([]);
  const [pendingAlias, setPendingAlias] = useState('');
  const [stepsOpen, setStepsOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);

  useEffect(() => {
    if (!taskId) return;
    let alive = true;
    (async () => {
      try {
        const task = await api.getTask(taskId);
        if (!alive) return;
        const r = task.recurrence;
        setSource(task.source);
        setMealLabel(task.mealLabel ?? null);
        setTitle(task.title);
        setNotes(task.notes ?? '');
        setChecklist(task.checklist.join('\n'));
        setAliases(task.aliases);
        setCategory(task.category);
        setAssignee(task.assigneePublicId ?? '');
        setType(r.type);
        setStart(r.startDate);
        setIntervalText(String(r.interval || 1));
        if (r.days.length > 0) setDays(r.days);
        setLastDay(r.dayOfMonth === -1);
        setDayOfMonth(r.dayOfMonth && r.dayOfMonth > 0 ? String(r.dayOfMonth) : '');
        setTimes(String(r.timesPerWeek ?? 1));
        setHasEnd(!!r.endDate);
        if (r.endDate) setEnd(r.endDate);
        setHasTime(!!task.dueTime);
        if (task.dueTime) setDueTime(time(task.dueTime));
        setAlertKind(task.alert);
        setAlertMinutes(ALERT_TIMES.some(a => a.minutes === task.alertMinutesBefore) ? task.alertMinutesBefore : 0);
        // Opened when there is something in it, so it is not missed
        setStepsOpen(task.checklist.length > 0);
      } catch (e: any) {
        if (alive) setError(e.message);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [taskId]);

  // Automatic tasks follow their source: only the person, notes and steps can change
  const automatic = source !== 'Manual';
  // Flexible tasks are recalled the evening before their window ends
  const showAlertTime = alertKind !== 'None' && type !== 'FlexibleWeekly';
  const showInterval = type === 'Daily' || type === 'Weekly' || type === 'Monthly';
  const intervalLabel = type === 'Weekly' ? 'A cada quantas semanas' : type === 'Monthly' ? 'A cada quantos meses' : 'A cada quantos dias';

  const build = (): T.SaveTaskRequest | string => {
    const name = title.trim();
    if (!name) return 'Indica a tarefa.';
    const recurrence: T.Recurrence = {
      type, startDate: start, endDate: type !== 'None' && hasEnd ? end : null, interval: 1, days: [], dayOfMonth: null, timesPerWeek: null,
    };
    if (showInterval) {
      const value = Number(interval);
      if (!Number.isInteger(value) || value < 1) return 'O intervalo deve ser um número a partir de 1.';
      recurrence.interval = value;
    }
    if (type === 'Weekly') {
      if (days.length === 0) return 'Escolhe pelo menos um dia.';
      recurrence.days = WEEK_DAYS.filter(d => days.indexOf(d) >= 0);
    }
    if (type === 'Monthly') {
      const day = Number(dayOfMonth);
      if (lastDay) recurrence.dayOfMonth = -1;
      else if (Number.isInteger(day) && day >= 1 && day <= 31) recurrence.dayOfMonth = day;
      else return 'Indica o dia do mês (1–31) ou escolhe o último dia.';
    }
    if (type === 'FlexibleWeekly') {
      const value = Number(times);
      if (!Number.isInteger(value) || value < 1 || value > 7) return 'Entre 1 e 7 vezes por semana.';
      recurrence.timesPerWeek = value;
    }
    return {
      title: name,
      notes: notes.trim() ? notes : null,
      category,
      assigneePublicId: assignee || null,
      dueTime: hasTime ? toTimeOnly(dueTime) : null,
      recurrence,
      checklist: checklist.split('\n').map(l => l.trim()).filter(Boolean),
      aliases: withPendingAlias(aliases, pendingAlias),
      alert: alertKind,
      alertMinutesBefore: showAlertTime ? alertMinutes : 0,
    };
  };

  const save = async () => {
    const request = build();
    if (typeof request === 'string') { setError(request); return; }
    setBusy(true);
    setError(null);
    try {
      if (taskId) await api.updateTask(taskId, request); else await api.createTask(request);
      goBack();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!taskId) return;
    if (!await confirm({ title: 'Apagar', text: `Apagar a tarefa "${title}" e as próximas vezes?`, confirmText: 'Apagar', danger: true })) return;
    setBusy(true);
    setError(null);
    try {
      await api.deleteTask(taskId);
      goBack();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const toggleDay = (day: T.DayOfWeek) =>
    setDays(current => toggleIn(current, day));

  const stepCount = checklist.split('\n').filter(l => l.trim()).length;
  const stepsSummary = [stepCount > 0 ? (stepCount === 1 ? '1 passo' : `${stepCount} passos`) : '', notes.trim()].filter(Boolean).join(' · ') || 'Nenhum';
  const moreSummary = [categoryText(category), aliases.length > 0 ? `também: ${aliases.join(', ')}` : ''].filter(Boolean).join(' · ');

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>;

  return (
    <Box sx={{ maxWidth: 640, pb: 10 }}>
      <PageHeader title={isNew ? 'Nova tarefa' : 'Editar tarefa'} />
      <Stack spacing={2}>
        {automatic && (
          <Alert severity="info">
            Tarefa automática (ementa ou compras): o nome e a data seguem a origem. Podes mudar quem faz, as notas e os passos.
          </Alert>
        )}
        {mealLabel && <Typography variant="body2" color="text.secondary">Para: {mealLabel} (acontece uma só vez)</Typography>}
        <TextField
          label="Tarefa" placeholder="Ex.: Pôr o lixo lá fora" value={title} disabled={automatic} autoFocus={isNew}
          onChange={e => setTitle(e.target.value)} slotProps={{ htmlInput: { maxLength: 100 } }}
        />
        <TextField
          select label="Quem faz" value={assignee} onChange={e => setAssignee(e.target.value)}
          helperText="Opcional. Sem ninguém, a tarefa fica para quem puder."
        >
          <MenuItem value="">Ninguém em particular</MenuItem>
          {members.map(m => <MenuItem key={m.publicId} value={m.publicId}>{m.name}</MenuItem>)}
        </TextField>
        <TextField select label="Repetição" value={type} disabled={automatic || mealLabel !== null} onChange={e => setType(e.target.value as T.RecurrenceType)}>
          {RECURRENCES.map(r => <MenuItem key={r.value} value={r.value}>{r.text}</MenuItem>)}
        </TextField>
        <TextField
          type="date" label={type === 'None' ? 'Data' : 'A partir de'} value={start} disabled={automatic}
          onChange={e => setStart(e.target.value)} slotProps={{ inputLabel: { shrink: true } }}
        />
        {showInterval && (
          <TextField
            type="number" label={intervalLabel} value={interval} disabled={automatic}
            onChange={e => setIntervalText(e.target.value)} slotProps={{ htmlInput: { min: 1, inputMode: 'numeric' } }}
          />
        )}
        {type === 'Weekly' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>Dias</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {WEEK_DAYS.map(d => (
                <Chip
                  key={d} label={shortDay(d)} disabled={automatic} clickable
                  color="primary" variant={days.indexOf(d) >= 0 ? 'filled' : 'outlined'} onClick={() => toggleDay(d)}
                />
              ))}
            </Box>
          </Box>
        )}
        {type === 'Monthly' && (
          <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
            <TextField
              type="number" label="Dia do mês" placeholder="1–31" value={dayOfMonth} disabled={automatic || lastDay}
              onChange={e => setDayOfMonth(e.target.value)} slotProps={{ htmlInput: { min: 1, max: 31, inputMode: 'numeric' } }}
              sx={{ flexGrow: 1 }}
            />
            <FormControlLabel control={<Switch checked={lastDay} disabled={automatic} onChange={e => setLastDay(e.target.checked)} />} label="Último dia" />
          </Stack>
        )}
        {type === 'FlexibleWeekly' && (
          <TextField
            type="number" label="Quantas vezes por semana" value={times} disabled={automatic}
            onChange={e => setTimes(e.target.value)} slotProps={{ htmlInput: { min: 1, max: 7, inputMode: 'numeric' } }}
          />
        )}
        {type !== 'None' && (
          <Box>
            <FormControlLabel
              control={<Switch checked={hasEnd} disabled={automatic} onChange={e => setHasEnd(e.target.checked)} />}
              label="Termina numa data"
            />
            {hasEnd && (
              <TextField
                type="date" label="Termina a" value={end} disabled={automatic} fullWidth sx={{ mt: 1 }}
                onChange={e => setEnd(e.target.value)} slotProps={{ inputLabel: { shrink: true }, htmlInput: { min: start } }}
              />
            )}
          </Box>
        )}
        <Box>
          <FormControlLabel
            control={<Switch checked={hasTime} disabled={automatic} onChange={e => setHasTime(e.target.checked)} />}
            label="A uma hora certa"
          />
          {hasTime && (
            <TextField
              type="time" label="Hora" value={dueTime} disabled={automatic} fullWidth sx={{ mt: 1 }}
              onChange={e => setDueTime(e.target.value)} slotProps={{ inputLabel: { shrink: true } }}
            />
          )}
        </Box>

        <Box>
          <TextField
            select fullWidth label="Aviso no telemóvel" value={alertKind}
            onChange={e => setAlertKind(e.target.value as T.TaskAlert)} helperText={ALERT_HINTS[alertKind]}
          >
            {TASK_ALERTS.map(a => <MenuItem key={a.value} value={a.value}>{a.text}</MenuItem>)}
          </TextField>
          {showAlertTime && (
            <TextField
              select fullWidth label="Quando" value={alertMinutes} sx={{ mt: 2 }}
              onChange={e => setAlertMinutes(Number(e.target.value))}
            >
              {ALERT_TIMES.map(a => <MenuItem key={a.minutes} value={a.minutes}>{a.text}</MenuItem>)}
            </TextField>
          )}
        </Box>

        <Box>
          <Accordion expanded={stepsOpen} onChange={(_, open) => setStepsOpen(open)} disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>Passos e notas</Typography>
                {!stepsOpen && <Typography variant="caption" color="text.secondary" noWrap component="div">{stepsSummary}</Typography>}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField
                  multiline minRows={3} label="Passos (um por linha, opcional)" value={checklist}
                  placeholder={'Ex.: Livros\nLanche\nEquipamento de ginástica'} onChange={e => setChecklist(e.target.value)}
                />
                <TextField multiline minRows={2} label="Notas" placeholder="Opcional" value={notes} onChange={e => setNotes(e.target.value)} />
              </Stack>
            </AccordionDetails>
          </Accordion>
          <Accordion expanded={moreOpen} onChange={(_, open) => setMoreOpen(open)} disableGutters variant="outlined">
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>Mais opções</Typography>
                {!moreOpen && <Typography variant="caption" color="text.secondary" noWrap component="div">{moreSummary}</Typography>}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Stack spacing={2}>
                <TextField select label="Categoria" value={category} disabled={automatic} onChange={e => setCategory(e.target.value as T.TaskCategory)}>
                  {TASK_CATEGORIES.map(c => <MenuItem key={c.value} value={c.value}>{c.text}</MenuItem>)}
                </TextField>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>Outros nomes (voz)</Typography>
                  <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    Também a reconheço por estes nomes quando falas ou escreves.
                  </Typography>
                  <AliasEditor value={aliases} onChange={setAliases} pending={pendingAlias} onPendingChange={setPendingAlias} />
                </Box>
              </Stack>
            </AccordionDetails>
          </Accordion>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}
        {!isNew && !automatic && (
          <Button variant="outlined" color="error" onClick={remove} disabled={busy}>Apagar tarefa</Button>
        )}
      </Stack>

      {/* Always in reach */}
      <Paper
        elevation={6}
        sx={{ position: 'sticky', bottom: { xs: 64, md: 8 }, mt: 2, p: 1.5, display: 'flex', gap: 1, justifyContent: 'flex-end', zIndex: 2 }}
      >
        <Button onClick={goBack} disabled={busy}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={busy}>Guardar</Button>
      </Paper>
      {confirmDialog}
    </Box>
  );
};

const TaskEditRoute: React.FC = () => <NeedsHousehold><TaskEditPage /></NeedsHousehold>;
export default TaskEditRoute;
